import React, { useEffect, useState } from 'react'
import Container from '../../../v2/Container'
import Image from '../../../Image'
import landingPageImg from '../../../../assets/purchase-yearbooks-landing-page.jpg'
import useQueryString from '../../../../hooks/use-query-string'
import { Package, Price, Student } from '../../../../models'
import Fetching from './Fetching'
import ErrorState from './ErrorState'
import YearbookForm from './YearbookForm'
import useLocalStorage from '../../../../hooks/useLocalStorage';
import Typography from '../../../Typography';
import StudentPromoImage from './StudentPromoImage';
import usePastImages from '../../../../hooks/usePastImages';
import { useCustomer } from '../../../../contexts/CustomerContext';
import routes from '../../../../routes';
import YearbookHomeForm from './YearbookHomeForm';
import { fetchYearbookHomeDelivery } from '../../../../api/yearbook_home_delivery';
import orderYearbookShipToHome from '../../../../assets/OrderYearbook_ShiptoHome_Personalized.jpg';

const ERROR = 'ERROR'
const DONE = 'DONE'
const FETCHING = 'FETCHING'
const TOO_MANY_RESULTS = 'TOO_MANY_RESULTS'

export const useStudent = (params) => {
  const [student, setStudent] = useState()
  const [status, setStatus] = useState(FETCHING)

  useEffect(() => {
    setStatus(FETCHING)

    Student
      .includes('school')
      .where({ ...params })
      .extraParams({ yba: true })
      .all()
      .then((res) => {
        if (res.data.length === 1) {
          setStudent(res.data[0])
          setStatus(DONE)
        } else {
          setStudent(res.data[res.data.length - 1])
          setStatus(DONE)
        }
      })
      .catch(() => {
        setStatus(ERROR)
      })
  }, [params.studentId, params.schoolId])

  return { student, status }
}

export const useYearbookPackage = (schoolId, campaign) => {
  const [yearbook, setYearbook] = useState();
  const [yearbooks, setYearbooks] = useState([])
  const [status, setStatus] = useState(FETCHING)

  useEffect(() => {
    if (!schoolId) return

    const priceScope = Price.where({ for_school: schoolId })

    Package
      .includes({ options: { prices: 'school' } })
      .merge({ prices: priceScope })
      .where({ schoolId })
      .where({ has_prices_for_school: schoolId })
      .where({ name: { match: 'yearbook' } })
      .all()
      .then((res) => {
        setYearbooks(res.data)
        setYearbook(res.data[0])
        setStatus(DONE)
      })
      .catch(() => setStatus(ERROR))
  }, [schoolId])

  return { yearbook, yearbooks, status }
}

// Once the in-window package lookup comes up empty, ask the backend whether
// the school is in ship-to-home mode. `enabled` gates the call so it only fires
// for expired schools, not every visit.
export const useYearbookHomeDelivery = (schoolId, enabled) => {
  const [homeDelivery, setHomeDelivery] = useState()
  const [status, setStatus] = useState(FETCHING)

  useEffect(() => {
    if (!enabled || !schoolId) return

    setStatus(FETCHING)

    fetchYearbookHomeDelivery(schoolId)
      .then((res) => {
        setHomeDelivery(res.data)
        setStatus(DONE)
      })
      .catch(() => setStatus(ERROR))
  }, [schoolId, enabled])

  return { homeDelivery, status }
}

const Yearbooks = () => {
  const { sch: schoolId, sid: studentId, campaign, message_id: messageId } = useQueryString()
  const { student, status } = useStudent({ schoolId, studentId })
  const { yearbook, yearbooks, status: packageStatus } = useYearbookPackage(schoolId, campaign)
  const needHome = Boolean(student) && packageStatus === DONE && !yearbook
  const { homeDelivery, status: homeStatus } = useYearbookHomeDelivery(schoolId, needHome)
  const [_, setIsUsingAccessCode] = useLocalStorage('isUsingAccessCode')
  const { customer, setCustomer } = useCustomer();

  useEffect(() => {
    if (!student?.email) return;

    setCustomer({ ...customer, email: student.email });
  }, [student]);

  const { data: pastImages } = usePastImages({ studentId: student?.id });

  setIsUsingAccessCode(true)

  if (status === FETCHING) return <Fetching />
  if (status === TOO_MANY_RESULTS || status === ERROR) {
    return <ErrorState message="We were unable to find your student. Please check back again later." />
  }

  // In-window: the normal ship-to-school flow.
  if (student && yearbook) {
    return (
      <Container maxWidth="md">
        <StudentPromoImage campaign={campaign || yearbook.campaign} studentId={student.id} />
        <YearbookForm
          student={student}
          school={student.school}
          defaultYearbook={yearbook}
          yearbooks={yearbooks}
          messageId={messageId}
        />
      </Container>
    )
  }

  // Student loaded but no current price window — wait for the home-delivery
  // lookup, then show the ship-to-home variant if the program is open.
  if (student && packageStatus !== DONE) return <Fetching />
  if (needHome && homeStatus === FETCHING) return <Fetching />

  if (homeDelivery && homeDelivery.state === 'home') {
    return (
      <Container maxWidth="md">
        <img
          src={orderYearbookShipToHome}
          alt="Don't miss out on this year's memories — personalize your cover! Yearbooks ship to your home, not the school."
          style={{ width: '100%', height: 'auto', display: 'block', marginBottom: '1.5rem' }}
        />
        <YearbookHomeForm
          student={student}
          school={student.school}
          homeDelivery={homeDelivery}
          messageId={messageId}
        />
      </Container>
    )
  }

  return (
    <Typography align="center" fontStyle="italic">
      The deadline to order yearbooks for this year has passed.  Yearbooks are no longer available.
    </Typography>
  )
}

export default Yearbooks
